import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faSliders } from "@fortawesome/pro-solid-svg-icons/faSliders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import { Territory } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, LoadingSpinner, SearchField } from "@mapmycustomers/ui";

import noTerritoriesIcon from "@app/assets/map/legends/noTerritoriesIcon.svg";
import ButtonLink from "@app/component/ButtonLink";
import NoTerritories from "@app/scene/map/components/NoTerritories";
import { enterTerritoryMode, setHighlightedTerritoryId } from "@app/scene/map/store/actions";
import BoundariesLayerData from "@app/scene/map/store/layers/boundaries/types/BoundariesLayerData";
import { getBoundariesLayerData } from "@app/scene/map/store/selectors";
import { isCurrentUserMember, isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getTerritories, getTerritoryCount } from "@app/store/territories";
import { hideTerritories } from "@app/store/territories/actions";
import { VISIBLE_TERRITORIES_LIMIT } from "@app/util/consts";

import FilterTerritoriesModal from "./FilterTerritoriesModal";
import styles from "./Territories.module.scss";
import TerritoryItem from "./TerritoryItem";

const messages = defineMessages({
  createLink: {
    id: "map.layer.boundaries.territories.createTerritory",
    defaultMessage: "<link>Create your first Territory</link>",
    description: "Title of the Boundaries legend when no territory available",
  },
  limitAlert: {
    id: "map.layer.boundaries.territories.limitAlert",
    defaultMessage:
      "Only {limit} {limit, plural, one {territory} other {territories}} can be displayed at the same time.",
    description: "Territories limit notification text",
  },
  searchPlaceholder: {
    id: "map.layer.boundaries.territories.search",
    defaultMessage: "Search",
    description: "Territories list search placeholder",
  },
  title: {
    id: "map.layer.boundaries.territories.title",
    defaultMessage: "Territories ({count}/{total})",
    description: "Territories title",
  },
});

interface Props {
  canCreateTerritories?: boolean;
  data: BoundariesLayerData;
  onEnterTerritoryMode?: typeof enterTerritoryMode;
  onUpdateVisibility?: (hiddenTerritoryIds: Territory["id"][]) => void;
  setHighlightedTerritoryId: typeof setHighlightedTerritoryId;
  territories: Territory[];
  total: number;
}

const Territories: React.FC<Props> = ({
  canCreateTerritories,
  data,
  onEnterTerritoryMode,
  onUpdateVisibility,
  setHighlightedTerritoryId,
  territories,
  total,
}) => {
  const intl = useIntl();

  const [query, setQuery] = useState("");
  const filteredTerritories = useMemo(() => {
    const q = query.trim().toLowerCase();
    return territories.filter(
      ({ name, territoryDetail }) =>
        !territoryDetail.hidden && (!q || name.toLowerCase().includes(q))
    );
  }, [territories, query]);

  const handleVisibilityChange = useCallback(
    (territoryId: Territory["id"] | undefined, hide: boolean, exclusively: boolean) => {
      if (territoryId) {
        let hiddenTerritoryIds = new Set(
          territories.filter(({ territoryDetail }) => territoryDetail.hidden).map(({ id }) => id)
        );
        if (hide) {
          // only hide given territoryId when exclusively
          if (exclusively) {
            hiddenTerritoryIds = new Set();
          }
          hiddenTerritoryIds.add(territoryId);
        } else {
          // only show given territoryId when exclusively
          if (exclusively) {
            hiddenTerritoryIds = new Set(territories.map(({ id }) => id));
          }
          hiddenTerritoryIds.delete(territoryId);
        }
        onUpdateVisibility?.(Array.from(hiddenTerritoryIds));
      } else {
        onUpdateVisibility?.(hide ? territories.map(({ id }) => id) : []);
      }
    },
    [territories, onUpdateVisibility]
  );

  const handleCreateTerritoryClick = useCallback(() => {
    onEnterTerritoryMode?.({ territoryId: "create" });
  }, [onEnterTerritoryMode]);

  const handleMouseEnter = useCallback(
    (territory: Territory) => {
      setHighlightedTerritoryId(territory.id);
    },
    [setHighlightedTerritoryId]
  );

  const handleMouseLeave = useCallback(() => {
    setHighlightedTerritoryId(undefined);
  }, [setHighlightedTerritoryId]);

  const handleTerritoryClick = useCallback(
    (territory: Territory) => {
      onEnterTerritoryMode?.({ territory, territoryId: territory.id });
    },
    [onEnterTerritoryMode]
  );

  const visibleTerritoriesCount = territories.filter(
    ({ territoryDetail }) => !territoryDetail.hidden
  ).length;

  const [filterTerritoriesModalVisible, showFilterTerritoriesModal, hideFilterTerritoriesModal] =
    useBoolean();

  return (
    <div className={styles.container}>
      {data.territoriesLoading ? (
        <LoadingSpinner />
      ) : territories.length ? (
        <div className={styles.territories}>
          <div className={styles.title}>
            <div>
              {intl.formatMessage(messages.title, { count: visibleTerritoriesCount, total })}
            </div>
            <Button
              className={styles.actionButton}
              icon={<FontAwesomeIcon icon={faSliders} />}
              iconOnly
              onClick={showFilterTerritoriesModal}
              type="default"
            />
          </div>
          <div className={styles.content}>
            <SearchField
              allowClear
              className={styles.search}
              onChange={setQuery}
              placeholder={intl.formatMessage(messages.searchPlaceholder)}
              size="middle"
              value={query}
            />
            <div className={styles.list}>
              {filteredTerritories.map((territory) => (
                <TerritoryItem
                  key={territory.id}
                  onClick={handleTerritoryClick}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onVisibilityChange={handleVisibilityChange}
                  showingTerritoryDisabled={visibleTerritoriesCount >= VISIBLE_TERRITORIES_LIMIT}
                  territory={territory}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <NoTerritories
          description={
            canCreateTerritories
              ? intl.formatMessage(messages.createLink, {
                  link: (text) => (
                    <ButtonLink onClick={handleCreateTerritoryClick}>{text}</ButtonLink>
                  ),
                })
              : undefined
          }
          icon={noTerritoriesIcon}
        />
      )}
      {filterTerritoriesModalVisible && (
        <FilterTerritoriesModal onHide={hideFilterTerritoriesModal} />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  canCreateTerritories: !isCurrentUserMember(state) && !isCurrentUserReadOnly(state),
  data: getBoundariesLayerData(state),
  territories: getTerritories(state),
  total: getTerritoryCount(state),
});

const mapDispatchToProps = {
  onEnterTerritoryMode: enterTerritoryMode,
  onUpdateVisibility: hideTerritories,
  setHighlightedTerritoryId,
};

export default connect(mapStateToProps, mapDispatchToProps)(Territories);
